<script setup lang="ts">
import type { CommandPaletteGroup, CommandPaletteItem } from '@nuxt/ui';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BreadcrumbSwitcherButton from '#layers/ui/components/BreadcrumbSwitcherButton.vue';
import { useFoldedSwitcherPopover } from '#layers/ui/composables/use-folded-switcher-popover';

interface Props {
	label: string;
	groups: CommandPaletteGroup<CommandPaletteItem>[];
	folded?: boolean;
}

const { label, groups, folded = false } = defineProps<Props>();

const { t } = useI18n();
const { switcherContent } = useFoldedSwitcherPopover(() => folded);

const open = ref(false);

const selectedCommand = computed(() =>
	groups.flatMap((group) => group.items ?? []).find((item) => item.active),
);

// The palette caps results at 12 by default
const resultLimit = computed(() => groups.reduce((count, group) => count + (group.items?.length ?? 0), 0));
</script>

<template>
	<DCommandPopover
		:open
		:model-value="selectedCommand"
		by="label"
		:placeholder="t('common.search')"
		:content="switcherContent"
		:popover="{ ui: { content: 'min-w-55 max-w-80' } }"
		:groups
		:fuse="{ resultLimit }"
		:ui="{
			group: 'first:max-h-84 first:overflow-y-auto',
		}"
		@update:open="(value) => open = Boolean(value)"
		@update:model-value="open = false"
	>
		<BreadcrumbSwitcherButton
			:folded
			:label
			:pressed="open"
		/>

		<template #item="{ item }">
			<div class="flex w-full min-w-0 items-center gap-2">
				<DIconOrImage
					v-if="item.icon"
					:icon="item.icon"
					class="size-4 flex-none text-toned"
				/>
				<span class="truncate">
					{{ item.label }}
				</span>
				<DIcon
					v-if="item.active"
					name="lucide:check"
					class="text-normal ms-auto size-4 flex-none"
				/>
			</div>
		</template>
	</DCommandPopover>
</template>
